<template>
    <div>
        <h1 class="font-bold text-4xl mb-20">Add a new digital nomad friendly workplace</h1>

        <form
            v-if="city !== null && !success"
            @submit.prevent="addWorkplace"
        >
            <FormInput
                :model-value="`${city.name} (${country.name})`"
                class="cursor-not-allowed"
                disabled
                label="City"
            />

            <FormInput
                v-model="workplace.name"
                :disabled="addingWorkplace"
                :errors="errors.name"
                :required='false'
                help-text="Enter the full name of the cafe or venue here"
                autocomplete="off"
                label="Name"
                @change="nameUpdate"
            />

            <FormInput
                v-model="workplace.address"
                :disabled="addingWorkplace"
                :errors="errors.address"
                :required='false'
                help-text="Enter the full address of the workplace, so that we can link to it on Google Maps"
                autocomplete="off"
                label="Address"
                @change="addressUpdate"
            >
                <template #after-input>
                    <p v-if="suggestedAddress"
                       class="italic my-10">
                        Did you mean: <a
                        class="underline font-bold text-indigo-400 transition-colors hover:text-pink-500 focus:text-pink-500"
                        href="#"
                        @click.prevent="applySuggestedAddress">{{ suggestedAddress }}</a>
                    </p>
                    <p v-else-if="automaticallyFilledAddress"
                       class="text-indigo-400 my-10">
                        We think that this address may belong to the workplace that you're adding right now. Please
                        verify if this is correct.
                    </p>
                    <p v-if="!showLatLon && this.workplace.longitude" class="text-indigo-400 my-10">
                        Is the location on the map incorrect? You can <a
                        class="underline font-bold text-indigo-400 transition-colors hover:text-pink-500 focus:text-pink-500"
                        href="#"
                        @click.prevent="useCurrentLocation">try to use your current location</a> or
                        <a class="underline font-bold text-indigo-400 transition-colors hover:text-pink-500 focus:text-pink-500"
                           href="#"
                           @click.prevent="showLatLon = true">manually enter the correct coordinates</a>.
                    </p>
                </template>
            </FormInput>

            <div
                :class="{ 'bg-gray-100': true }"
                class="my-20 h-[300px] relative"
            >
                <button
                    v-if="canUseGPS"
                    :class="{ 'cursor-wait': addingWorkplace  || lookingUpAddress || lookingUpGps}"
                    :disabled="addingWorkplace || lookingUpAddress || lookingUpGps"
                    class="bg-indigo-400 px-10 py-5 text-[12px] text-white rounded transition-colors hover:bg-pink-500 focus:bg-pink-500 absolute left-5 bottom-20 md:bottom-5 z-[500]"
                    type="button"
                    @click="useCurrentLocation">
                    Use my current location
                </button>

                <l-map v-if="city"
                       ref="map"
                       v-model:zoom="zoom"
                       :center="mapCenter"
                       :max-zoom="19"
                       :options="{attributionControl: false}"
                       fade-animation>
                    <l-tile-layer
                        attribution="© <a target='_blank' href='https://www.openstreetmap.org/copyright'>OpenStreetMap</a> contributors"
                        layer-type="base"
                        name="OpenStreetMap"
                        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    ></l-tile-layer>

                    <l-control-attribution
                        position="bottomright"
                        prefix=""
                    />

                    <l-marker
                        v-if="workplace.latitude && workplace.longitude"
                        :lat-lng="[workplace.latitude, workplace.longitude]"
                        :name='workplace.name'
                    >
                        <!--                        <l-popup>-->
                        <!--                            <a :href="`https://www.google.com/maps/search/${encodeURI(cafe.address)}`"-->
                        <!--                               class="text-blue-400 underline"-->
                        <!--                               target="_blank">-->
                        <!--                                {{ cafe.name }}-->
                        <!--                            </a>-->
                        <!--                        </l-popup>-->
                    </l-marker>
                </l-map>
            </div>

            <div v-if="showLatLon">
                <p class="my-10 text-white px-10 py-5 bg-pink-500 rounded">
                    We're not exactly sure about the coordinates of the workplace you're adding right now. Can you
                    please enter them for us?
                </p>
                <FormInput
                    v-model="workplace.latitude"
                    :disabled="addingWorkplace"
                    :errors="errors.latitude"
                    :required='false'
                    help-text="Latitude of the workplace, so that we can display it on a map. You can get the latitude on Google Maps: It copies a string for you in the format (latitude, longitude), which you can paste in this field."
                    label="Latitude"
                    step="any"
                    type="number"
                    @paste="pasteCoords($event)"
                />

                <FormInput
                    v-model="workplace.longitude"
                    :disabled="addingWorkplace || lookingUpAddress"
                    :errors="errors.longitude"
                    :required='false'
                    help-text="Longitude of the workplace, so that we can display it on a map. You can get the longitude on Google Maps: It copies a string for you in the format (latitude, longitude), which you can paste in this field."
                    label="Longitude"
                    step="any"
                    type="number"
                    @paste="pasteCoords($event)"
                />
            </div>

            <FormSelect
                v-model="workplace.tags"
                :disabled="addingWorkplace"
                :errors="errors.tags"
                :options="tags"
                label="Tags"
                option-label="label"
                multiple
            />

            <FormSelect
                v-model="workplace.workplace_type_id"
                :disabled="addingWorkplace"
                :errors="errors.workplace_type_id"
                :options="workplaceTypes"
                label="Type"
                option-label="label"
            />

            <template v-if="user.can_verify">
                <FormInput
                    v-model="workplace.credit_to_name"
                    :disabled="addingWorkplace"
                    :errors="errors.credit_to_name"
                    :required='false'
                    help-text="(Optional) The name of the source you'd like to credit for the discovery or addition of this workplace"
                    label="Credit to"
                />

                <FormInput
                    v-model="workplace.credit_to_url"
                    :disabled="addingWorkplace"
                    :errors="errors.credit_to_url"
                    :required='false'
                    help-text="(Optional) The URL of the source you're giving credit"
                    label="URL for credit"
                    type="text"
                />
            </template>

            <button
                :class="{ 'cursor-wait': addingWorkplace  || lookingUpAddress}"
                :disabled="addingWorkplace || lookingUpAddress"
                class="bg-indigo-400 px-15 py-10 text-white rounded transition-colors hover:bg-pink-500 focus:bg-pink-500"
                type="submit">
                Add workplace
            </button>

            <p
                v-if="Object.keys(errors).length > 0"
                class="text-red-500">
                The form contains errors. Please correct them.
            </p>

            <p v-if="osmAttribution"
               class="text-[12px] mt-20 opacity-75">Geographical data has been sourced from
                <a class="underline" href="https://openstreetmap.org/copyright" rel="noopener nofollow" target="_blank">OpenStreetMap</a>.
            </p>
        </form>

        <div v-else-if="success">
            <h3 class="font-bold text-2xl mb-20">🎉 Thank you!</h3>
            <p>
                Your workplace has been added to our list! You can view it
                <router-link
                    :to="workplacePath ?? `/cafes/${countrySlug}/${citySlug}`"
                    class="text-indigo-400 underline hover:text-pink-500 focus:text-pink-500">
                    here.
                </router-link>
            </p>
        </div>
    </div>
</template>

<script>
import FormInput from "../../../components/FormInput";
import axios from "axios";
import FormSelect from "../../../components/FormSelect";
import {LControlAttribution, LMap, LMarker, LPopup, LTileLayer} from "@vue-leaflet/vue-leaflet";
import {useMeta} from "vue-meta";
import {mapState} from "vuex";

export default {
    name: "AddNewWorkplace",
    components: {FormSelect, FormInput, LMap, LTileLayer, LMarker, LPopup, LControlAttribution},
    props: {
        countrySlug: String,
        citySlug: String,
    },
    data() {
        return {
            addingWorkplace: false,
            errors: {},
            city: null,
            country: null,
            workplace: {
                tags: [],
                address: '',
                latitude: null,
                longitude: null,
                workplace_type_id: null,
                credit_to_name: null,
                credit_to_url: null
            },
            loading: false,
            showLatLon: false,
            lookingUpAddress: false,
            suggestedAddress: null,
            suggestedCoordinates: null,
            automaticallyFilledAddress: false,
            tags: [],
            workplaceTypes: [],
            success: false,
            osmAttribution: false,
            lookingUpGps: false,
            workplacePath: null,
            zoom: 12.5
        }
    },
    computed: {
        mapCenter() {
            if (this.workplace.latitude && this.workplace.longitude) {
                return [this.workplace.latitude, this.workplace.longitude];
            }

            return [this.city.latitude, this.city.longitude];
        },
        canUseGPS() {
            return 'geolocation' in navigator;
        },
        ...mapState([
            'user'
        ])
    },
    watch: {
        'workplace.latitude'(latitude) {
            if (latitude && this.workplace.longitude) {
                this.zoom = 17;
            } else {
                this.zoom = 12.5;
            }
        }
    },
    methods: {
        useCurrentLocation() {
            this.lookingUpGps = true;

            window.navigator.geolocation.getCurrentPosition((position) => {
                const crd = position.coords;

                this.workplace.longitude = crd.longitude;
                this.workplace.latitude = crd.latitude;

                this.getCoordinates(`${crd.latitude}, ${crd.longitude}`, response => {
                    if (response.data.length > 0) {
                        this.osmAttribution = true;

                        const firstMatch = response.data[0];

                        const foundAddress = firstMatch.display_name;
                        // const latitude = firstMatch.lat;
                        // const longitude = firstMatch.lon;

                        this.workplace.address = foundAddress;
                    } else {
                        // this.workplace.address = null;
                        // this.workplace.latitude = null;
                        // this.workplace.longitude = null;
                    }
                }, true);

                this.lookingUpGps = false;
            }, (error) => {
                console.warn(`Error while trying to request the current user's location (${error.code}): ${error.message}`);
                this.lookingUpGps = false;
            }, {
                enableHighAccuracy: true,
                timeout: 30 * 1000,
                maximumAge: 60 * 60 * 3
            });
        },
        addWorkplace() {
            if (this.addingWorkplace || this.lookingUpAddress) return;

            this.addingWorkplace = true;

            axios.post(`/api/countries/${this.countrySlug}/cities/${this.citySlug}/workplaces`, this.workplace).then(response => {
                this.success = true;
                this.workplacePath = response.data.url;
                this.errors = {};
            }).catch(error => {
                this.errors = error.response?.data?.errors ?? {};
                console.error(error);
                // alert("Sorry, something went wrong:\n" + error);
            }).finally(() => {
                this.addingWorkplace = false;
            });
        },
        getCity() {
            this.loading = true;

            axios.get(`/api/countries/${this.countrySlug}/cities/${this.citySlug}`).then(response => {
                this.city = response.data.city;
                this.country = response.data.country;
            }).catch(error => {
                alert("Sorry, something went wrong:\n" + error);
            }).finally(() => {
                this.loading = false;
            });
        },
        getTags() {
            axios.get('/api/tags').then(response => {
                this.tags = response.data;
            }).catch(error => {
                console.error(error);
            });
        },
        getWorkplaceTypes() {
            axios.get('/api/workplace-types').then(response => {
                this.workplaceTypes = response.data;
                this.workplace.workplace_type_id = this.workplaceTypes[0].id;
            }).catch(error => {
                console.error(error);
            });
        },
        pasteCoords($event) {
            let clipboardData = $event.clipboardData || window.clipboardData;
            let pastedData = clipboardData.getData('Text').replace('(', '').replace(')', '');

            let matches = pastedData.match(/^([-+]?[1-8]?\d(\.\d+)?|90(\.0+)?),\s*([-+]?(180(\.0+)?|((1[0-7]\d)|([1-9]?\d))(\.\d+)?))$/);

            if (matches !== null) {
                const lat = matches[1];
                const lon = matches[4];

                $event.stopPropagation();
                $event.preventDefault();

                this.workplace.latitude = lat;
                this.workplace.longitude = lon;
            }
        },
        getCoordinates(query, callback, exact = false) {
            this.lookingUpAddress = true;

            axios.post(`/api/countries/${this.countrySlug}/cities/${this.citySlug}/workplaces/coordinates`, {
                address: query,
                exact: exact
            }).then(callback).catch(error => {
                this.errors = error.response?.data?.errors ?? {};
                console.error(error);
                // alert("Sorry, something went wrong:\n" + error);
            }).finally(() => {
                this.lookingUpAddress = false;
            });
        },
        nameUpdate(name) {
            if (name.trim() !== '') {
                this.getCoordinates(name, response => {
                    this.automaticallyFilledAddress = false;
                    this.suggestedAddress = null;
                    this.suggestedCoordinates = null;

                    if (response.data.length > 0) {
                        this.osmAttribution = true;

                        const firstMatch = response.data[0];

                        const foundAddress = firstMatch.display_name;
                        const latitude = firstMatch.lat;
                        const longitude = firstMatch.lon;

                        // if (!this.workplace.address) {
                        this.workplace.address = foundAddress;
                        this.workplace.latitude = latitude;
                        this.workplace.longitude = longitude;
                        this.automaticallyFilledAddress = true;
                        // }
                    } else {
                        this.workplace.address = null;
                        this.workplace.latitude = null;
                        this.workplace.longitude = null;
                    }
                });
            }
        },
        applySuggestedAddress() {
            this.workplace.address = this.suggestedAddress;
            this.workplace.latitude = this.suggestedCoordinates.latitude;
            this.workplace.longitude = this.suggestedCoordinates.longitude;

            this.suggestedAddress = null;
            this.suggestedCoordinates = null;
        },
        addressUpdate(address) {
            if (address.trim() !== '') {
                this.getCoordinates(address, response => {
                    this.automaticallyFilledAddress = false;

                    if (response.data.length > 0) {
                        this.osmAttribution = true;

                        const firstMatch = response.data[0];

                        const foundAddress = firstMatch.display_name;
                        const latitude = firstMatch.lat;
                        const longitude = firstMatch.lon;

                        if (firstMatch.importance < 1) {
                            this.suggestedAddress = foundAddress;
                            this.suggestedCoordinates = {
                                latitude,
                                longitude
                            };
                        }

                        if (!this.workplace.latitude) {
                            this.workplace.latitude = latitude;
                            this.workplace.longitude = longitude;
                        }
                    } else {
                        if (!this.showLatLon) {
                            this.showLatLon = true;
                            this.workplace.latitude = null;
                            this.workplace.longitude = null;
                        }
                    }
                })
            } else {
                this.workplace.latitude = null;
                this.workplace.longitude = null;
            }
        }
    },
    created() {
        this.getCity();
        this.getTags();
        this.getWorkplaceTypes();
    },
    setup() {
        useMeta({
            title: `Add a new workplace – Digital Nomad Cafes`
        });
    }
}
</script>
